import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

export const escapeHtml = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[char])
  );

export const isTableMime = (mimeType) =>
  (mimeType || "").startsWith("text/csv");

/**
 * Where a table card goes, standing in for it while `marked` runs. The card is put in
 * afterwards so no cell is ever read as markdown — a `*`, `_` or blank line in the data
 * would otherwise be rendered rather than shown. The nonce is new on every render, so
 * nothing the model writes can match a slot.
 *
 * A card is a block, so a paragraph holding one is split around it: left inside, the
 * browser closes the paragraph at the card and the sentence's own full stop ("The full
 * list is here: [file].") was left below the table as a paragraph of its own.
 */
export function tableCardSlots() {
  const prefix = `oliver-table-card-${Math.random().toString(36).slice(2)}-`;
  const slot = new RegExp(`${prefix}(\\d+)`);
  const paragraph = /<p>([\s\S]*?)<\/p>/g;
  const split = (inner, cards) =>
    inner
      .split(slot)
      .map((part, i) => {
        if (i % 2) return cards[part] ?? "";
        const text = i ? part.replace(/^\s*[.,;:]+/, "") : part;
        return text.trim() ? `<p>${text.trim()}</p>` : "";
      })
      .join("");
  return {
    slot: (index) => `${prefix}${index}`,
    fill: (html, cards) =>
      html
        .replace(paragraph, (match, inner) =>
          slot.test(inner) ? split(inner, cards) : match
        )
        .replace(
          new RegExp(slot.source, "g"),
          (match, index) => cards[index] ?? match
        ),
  };
}

/** Columns whose values read as one token: never wrapped, numbers in tabular figures. */
const TABLE_CELL_CLASS = {
  id: "table-card__cell--number",
  integer: "table-card__cell--number",
  number: "table-card__cell--number",
  money: "table-card__cell--number",
  percent: "table-card__cell--number",
  year: "table-card__cell--number",
  quarter: "table-card__cell--nowrap",
  month: "table-card__cell--nowrap",
  date: "table-card__cell--nowrap",
  datetime: "table-card__cell--nowrap",
  time: "table-card__cell--nowrap",
  yes_no: "table-card__cell--nowrap",
};

const COLUMN_TYPES = [
  "id",
  "integer",
  "number",
  "money",
  "percent",
  "year",
  "quarter",
  "month",
  "date",
  "datetime",
  "time",
  "yes_no",
  "code",
  "list",
  "text",
];

/** The clinic's zone, the CRM's own APP_TIMEZONE, which its screens convert to as well. */
const CLINIC_TIME_ZONE = "America/Toronto";
// The CRM's most used date and date-time formats.
const DATE_FORMAT = "MMM D, YYYY";
const DATE_TIME_FORMAT = "MMM D, YYYY hh:mm A";
const ZONED = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/** Two decimals at most; below 1, two significant digits, so 0.0034 does not read as 0. */
function rounded(value) {
  const [whole, fraction] = value.split(".");
  if (!fraction) return value;
  const number = Number(`${whole.replace(/,/g, "")}.${fraction}`);
  const size = Math.abs(number);
  const places =
    size === 0 || size >= 1
      ? 2
      : Math.min(20, Math.max(2, 1 - Math.floor(Math.log10(size))));
  return number.toFixed(places).replace(/\.?0+$/, "");
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const grouped = (value) =>
  value.replace(
    /^(-?)(\d{4,})/,
    (_, sign, whole) => sign + whole.replace(/\B(?=(\d{3})+$)/g, ",")
  );

const NUMERIC = /^-?(\d{1,3}(,\d{3})+|\d+)(\.\d+)?$/;
const plain = (value) => Number(value.replace(/,/g, ""));
const when = (valid, format) => (value) => valid(value) ? format(value) : value;

/** A cell as the CRM shows it, by its column's declared type; a value the type cannot read stays as written. Downloads keep the file's value. */
const FORMAT = {
  number: when(
    (v) => NUMERIC.test(v),
    (v) => grouped(rounded(v))
  ),
  money: when(
    (v) => NUMERIC.test(v),
    (v) => `$${grouped(plain(v).toFixed(2))}`.replace("$-", "-$")
  ),
  percent: when(
    (v) => NUMERIC.test(v),
    (v) => `${grouped(rounded(v))}%`
  ),
  quarter: when(
    (v) => /^\d{4}-Q[1-4]$/.test(v),
    (v) => v.replace(/^(\d{4})-Q(\d)$/, "Q$2 $1")
  ),
  month: when(
    (v) => /^\d{4}-(0[1-9]|1[0-2])$/.test(v),
    (v) => `${MONTHS[Number(v.slice(5)) - 1]} ${v.slice(0, 4)}`
  ),
  date: (v) => dateDisplay(v),
  datetime: (v) => dateDisplay(v),
  time: when(
    (v) => /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d(\.\d+)?)?$/.test(v),
    (v) => dayjs(`2000-01-01T${v}`).format("h:mm A")
  ),
};

function cellDisplay(type, value) {
  return value === "" ? "" : (FORMAT[type] || ((v) => v))(value);
}

function dateDisplay(value) {
  // Read as written first: a value dayjs would roll over (Feb 30, 25:00) stays as it is.
  const written = value.slice(0, 16).replace("T", " ");
  const hasTime = written.length > "YYYY-MM-DD".length;
  const wall = dayjs(written);
  if (wall.format(hasTime ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD") !== written) {
    return value;
  }
  const parsed = ZONED.test(value) ? dayjs(value) : wall;
  if (!parsed.isValid()) return value;
  const date = ZONED.test(value) ? parsed.tz(CLINIC_TIME_ZONE) : parsed;
  return date.format(hasTime ? DATE_TIME_FORMAT : DATE_FORMAT);
}

const countLabel = (count, noun) =>
  `${count.toLocaleString("en-US")} ${noun}${count === 1 ? "" : "s"}`;

/** The rows the server parsed, or null when the response is not a table. */
export function parseTable(data) {
  if (!Array.isArray(data?.columns) || !Array.isArray(data?.rows)) return null;
  if (!data.rows.every(Array.isArray)) return null;
  const shown = data.rows.length;
  return {
    columns: data.columns.map((column) => ({
      name: String(column?.name ?? ""),
      label: String(column?.label ?? column?.name ?? ""),
      type: COLUMN_TYPES.includes(column?.type) ? column.type : "text",
    })),
    rows: data.rows,
    cites: Array.isArray(data.cites) ? data.cites : [],
    shown,
    total: Number.isInteger(data.total) ? Math.max(data.total, shown) : shown,
  };
}

/** Each cell's citation tokens, by "row:column", as c3 checked them when the table was saved. */
function citesByCell(table) {
  return new Map(
    (table.cites || []).map(([row, col, tokens]) => [`${row}:${col}`, tokens])
  );
}

/**
 * A table Oliver made. The column's type decides how a cell reads; `marks` turns a cell's
 * citation tokens into what follows its text, or nothing.
 */
export function tableCardHtml(title, table, { url, filename }, marks = null) {
  const label = escapeHtml(title);
  const classes = table.columns.map(({ type }) =>
    TABLE_CELL_CLASS[type] ? ` class="${TABLE_CELL_CLASS[type]}"` : ""
  );
  const head = table.columns
    .map(({ label }) => `<th scope="col">${escapeHtml(label)}</th>`)
    .join("");
  const cites = marks ? citesByCell(table) : new Map();
  const cell = (value, i, r) => {
    const cited = cites.get(`${r}:${i}`);
    const after = cited ? marks(cited) : "";
    if (value == null) return `<td${classes[i]}>${after}</td>`;
    const shown = cellDisplay(table.columns[i].type, String(value));
    return `<td${classes[i]}>${escapeHtml(shown)}${
      after ? ` ${after}` : ""
    }</td>`;
  };
  const body = table.rows
    .map(
      (row, r) =>
        `<tr>${classes.map((_, i) => cell(row[i], i, r)).join("")}</tr>`
    )
    .join("");
  const partial =
    table.total > table.shown
      ? `<span>First ${table.shown.toLocaleString("en-US")} of ${countLabel(
          table.total,
          "row"
        )}</span>`
      : "";
  return (
    `<div class="table-card">` +
    `<div class="table-card__title"><i class="fas fa-table"></i>${label}` +
    `<button type="button" class="table-card__expand" title="Expand"><i class="fas fa-expand"></i></button></div>` +
    `<div class="table-card__scroll" tabindex="0">` +
    `<table aria-label="${label}"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>` +
    `</div>` +
    `<div class="table-card__footer">` +
    `<span>${countLabel(table.total, "row")} · ${countLabel(
      table.columns.length,
      "column"
    )}</span>` +
    `<span class="table-card__actions">${partial}` +
    `<a href="${escapeHtml(url)}" download="${escapeHtml(
      filename || title
    )}" class="table-card__download"><i class="fas fa-download"></i>Download</a>` +
    `</span>` +
    `</div>` +
    `</div>`
  );
}

/** A table card's slot while its rows are still coming. */
export function tableCardSkeletonHtml(title) {
  return (
    `<div class="table-card" aria-busy="true">` +
    `<div class="table-card__title"><i class="fas fa-table"></i>${escapeHtml(
      title
    )}</div>` +
    `<span class="artifact-skeleton table-card__skeleton"></span>` +
    `</div>`
  );
}

const markdownCell = (value) =>
  value == null
    ? ""
    : String(value)
        .replace(/\|/g, "\\|")
        .replace(/\r\n|\r|\n/g, "<br>");

/**
 * A table card as plain text: a markdown table of the rows it shows, set apart from the
 * text around it, so a paste outside Oliver carries the data rather than a reference. Each
 * cell reads as the card shows it.
 */
export function tableCardMarkdown(title, table, marks = null) {
  const line = (cells) => `| ${cells.join(" | ")} |`;
  const cites = marks ? citesByCell(table) : new Map();
  const shown = (value, i, r) => {
    const cited = cites.get(`${r}:${i}`);
    const after = cited ? marks(cited) : "";
    const text =
      value == null ? "" : cellDisplay(table.columns[i].type, String(value));
    return after ? `${text} ${after}`.trim() : value == null ? value : text;
  };
  const lines = [
    title,
    "",
    line(table.columns.map(({ label }) => markdownCell(label))),
    line(table.columns.map(() => "---")),
    ...table.rows.map((row, r) =>
      line(table.columns.map((_, i) => markdownCell(shown(row[i], i, r))))
    ),
  ];
  if (table.total > table.shown) {
    lines.push(
      "",
      `First ${table.shown.toLocaleString("en-US")} of ${countLabel(
        table.total,
        "row"
      )}. Download for the full file.`
    );
  }
  return `\n\n${lines.join("\n")}\n\n`;
}
